// A keep-upload belongs to the app, not to the page that started it, so leaving that page
// never drops it (wiki/feature-upload-reliability.md §"Background uploads").
import { untrack } from 'svelte';
import { UiKeys, type Practice } from '@voicedojo/backend-types';
import { ApiError, isAbortError } from '$lib/api/client';
import { bareRecordingId } from '$lib/api/recordings';
import { uploadTake, uploadThumbnailBestEffort, type TakeUploadOptions } from '$lib/api/uploads';
import { t } from '$lib/i18n';

// `sending` runs until upload-complete answers; `confirming` waits on the server's push
// because the answer did not report the upload recorded.
export type TakeUploadPhase = 'sending' | 'confirming' | 'saved' | 'failed';

export type KeepUploadOptions = Omit<TakeUploadOptions, 'onProgress' | 'onServerState'>;

// `thumbnail` is a frame already captured for the take; without one the upload captures its own.
type Request = {
  takeId: string;
  blob: Blob;
  contentType: string;
  options: KeepUploadOptions;
  auto: boolean;
  thumbnail?: Blob | null;
};

export class TakeUpload {
  phase = $state<TakeUploadPhase>('sending');
  percent = $state<number | null>(null);
  // The send's failure, or null when the server never confirmed a sent take.
  error = $state<unknown>(null);
  auto = $state(false);
  retryable = $state(false);
  announcing = $state(false);
  practice = $state<Practice | undefined>(undefined);

  constructor(readonly takeId: string) {}
}

export const SAVED_NOTICE_MS = 4000;

const key = (takeId: string): string => bareRecordingId(takeId).toLowerCase();

export class TakeUploads {
  #entries = $state<Record<string, TakeUpload>>({});
  #watchers = $state<Record<string, number>>({});
  // Not reactive: `retryable` is what the views read.
  #requests: Record<string, Request> = {};
  #unloadGuarded = false;
  #noticeTimers: Record<string, ReturnType<typeof setTimeout>> = {};
  #noticesHeld = false;

  get(takeId: string): TakeUpload | undefined {
    return this.#entries[key(takeId)];
  }

  // Only while bytes or the complete are still in flight: after that the server has the take,
  // or the journal resumes it on the next visit.
  get sending(): boolean {
    return Object.values(this.#entries).some((e) => e.phase === 'sending');
  }

  // The uploads no mounted page is showing: the app-level indicator draws these.
  get background(): TakeUpload[] {
    return Object.entries(this.#entries)
      .filter(([id, e]) => !this.#watchers[id] && (e.phase !== 'saved' || e.announcing))
      .map(([, e]) => e);
  }

  // Single-flight per take: a take already sending, confirming or saved is never sent again.
  start(
    takeId: string,
    blob: Blob,
    contentType: string,
    options: KeepUploadOptions,
    auto: boolean,
    thumbnail?: Blob | null,
  ): TakeUpload {
    const id = key(takeId);
    const existing = this.#entries[id];
    if (existing && existing.phase !== 'failed') return existing;
    const entry = existing ?? new TakeUpload(takeId);
    entry.phase = 'sending';
    entry.percent = null;
    entry.error = null;
    entry.auto = auto;
    entry.retryable = false;
    entry.announcing = false;
    entry.practice = options.practice;
    this.#entries[id] = entry;
    const request = { takeId, blob, contentType, options, auto, thumbnail };
    this.#requests[id] = request;
    this.#guardUnload();
    void this.#run(id, entry, request);
    return entry;
  }

  retry(takeId: string): void {
    const request = this.#requests[key(takeId)];
    if (request && this.get(takeId)?.phase === 'failed') {
      this.start(request.takeId, request.blob, request.contentType, request.options, request.auto, request.thumbnail);
    }
  }

  dismiss(takeId: string): void {
    const id = key(takeId);
    delete this.#entries[id];
    delete this.#requests[id];
  }

  // A Saved notice never times out under the pointer: it is what the speaker is reaching for.
  holdNotices(): void {
    this.#noticesHeld = true;
    Object.values(this.#noticeTimers).forEach(clearTimeout);
  }

  releaseNotices(): void {
    if (!this.#noticesHeld) return;
    this.#noticesHeld = false;
    for (const [id, entry] of Object.entries(this.#entries)) {
      if (entry.announcing) this.#endNoticeLater(id, entry);
    }
  }

  // A page showing this take's state; while one is mounted the indicator stays out of it.
  // Untracked, so an effect that calls it does not re-run on its own count.
  watch(takeId: string): () => void {
    const id = key(takeId);
    const add = (n: number): void =>
      untrack(() => {
        const count = (this.#watchers[id] ?? 0) + n;
        if (count > 0) this.#watchers[id] = count;
        else delete this.#watchers[id];
      });
    add(1);
    return () => add(-1);
  }

  async #run(id: string, entry: TakeUpload, request: Request): Promise<void> {
    const { takeId, blob, contentType, options, auto, thumbnail } = request;
    let sent: Awaited<ReturnType<typeof uploadTake>>;
    try {
      sent = await uploadTake(takeId, blob, contentType, {
        ...options,
        onProgress: (p) => (entry.percent = p),
      });
    } catch (e) {
      // Discard stopped it: that is the user's own doing, so it leaves nothing behind.
      if (isAbortError(e)) return this.#forget(id, entry);
      entry.phase = 'failed';
      entry.error = e;
      entry.percent = null;
      entry.retryable = true;
      if (auto && e instanceof ApiError && e.status === 0) {
        window.addEventListener('online', () => this.retry(takeId), { once: true });
      }
      if (!(e instanceof ApiError)) throw e;
      return;
    }
    void uploadThumbnailBestEffort(sent.recordingId, blob, thumbnail);
    entry.percent = null;
    if (sent.stored) this.#saved(id, entry);
    else entry.phase = 'confirming';
    sent.confirmed.then(
      (ok) => {
        if (this.#entries[id] !== entry || entry.phase === 'failed') return;
        if (ok) {
          if (entry.phase === 'confirming') this.#saved(id, entry);
          return;
        }
        entry.phase = 'failed';
        entry.error = null;
      },
      (e: unknown) => {
        if (isAbortError(e)) this.#forget(id, entry);
      },
    );
  }

  #saved(id: string, entry: TakeUpload): void {
    entry.phase = 'saved';
    entry.announcing = true;
    delete this.#requests[id];
    if (!this.#noticesHeld) this.#endNoticeLater(id, entry);
  }

  #endNoticeLater(id: string, entry: TakeUpload): void {
    clearTimeout(this.#noticeTimers[id]);
    this.#noticeTimers[id] = setTimeout(() => {
      entry.announcing = false;
      delete this.#noticeTimers[id];
    }, SAVED_NOTICE_MS);
  }

  #forget(id: string, entry: TakeUpload): void {
    if (this.#entries[id] !== entry) return;
    delete this.#entries[id];
    delete this.#requests[id];
  }

  #guardUnload(): void {
    if (this.#unloadGuarded || typeof window === 'undefined') return;
    this.#unloadGuarded = true;
    window.addEventListener('beforeunload', (e) => {
      if (this.sending) e.preventDefault();
    });
  }
}

export function uploadFailureMessage(upload: TakeUpload): string {
  const e = upload.error;
  if (e instanceof ApiError && e.status === 403) {
    // A 403 can also be a series that is not the caller's: say what the server said.
    return e.hasServerReason ? e.message : t(UiKeys.SaveToLibrary.NotAvailable);
  }
  // A rejected-as-malformed save states its own reason (e.g. the take's practice is unknown).
  if (e instanceof ApiError && e.status === 422) return e.message;
  return t(upload.auto ? UiKeys.SaveToLibrary.AutoUploadFailed : UiKeys.SaveToLibrary.Failed);
}

export const takeUploads = new TakeUploads();
